/**
 * The ward list. Phase 0's start page (PHASE-0.md §2.2).
 *
 * One row per bed, and **the safety flags are in the row** — not behind a click,
 * not below the fold. Thirty beds have to fit on a 1366×768 ward screen without
 * scrolling, because a flag that needs scrolling is a flag that gets missed
 * (AGENTS.md §5).
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatClinicalDateTime } from '../format/clinicalDate'
import {
  guarded,
  patientAge,
  patientIdentifier,
  patientName,
  safetyBadges,
} from '../patient/patientDisplay'
import { OpenPatient } from '../patient/OpenPatient'
import {
  fetchWardList,
  WardListError,
  type WardList as WardListData,
  type WardListEntry,
  type WardListFailure,
} from './wardApi'
import { WardPicker } from './WardPicker'
import { responsibleDoctor, type WardTranslate } from './wardDisplay'

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly list: WardListData }
  | { readonly kind: 'failed'; readonly failure: WardListFailure }

function Row({
  entry,
  today,
  onOpenPatient,
  t,
}: {
  readonly entry: WardListEntry
  readonly today: Date
  readonly onOpenPatient: (patientId: string) => void
  readonly t: WardTranslate
}) {
  return (
    <tr>
      <td className="ward-list__bed">{entry.bed}</td>
      <td className="ward-list__name">
        <OpenPatient
          name={patientName(entry, t)}
          onOpen={() => onOpenPatient(entry.patientId)}
          t={t}
        />
      </td>
      <td className="ward-list__identifier">{patientIdentifier(entry, t)}</td>
      <td className="ward-list__age">{guarded(() => patientAge(entry, t, today), t)}</td>
      <td className="ward-list__admitted">
        {guarded(() => formatClinicalDateTime(entry.admittedAt), t)}
      </td>
      <td className="ward-list__responsible">{responsibleDoctor(entry, t)}</td>
      <td className="ward-list__safety">
        <ul className="badges">
          {safetyBadges(entry, t).map((badge) => (
            <li key={badge.key} className={`badge badge--${badge.level}`}>
              {badge.label}
            </li>
          ))}
        </ul>
      </td>
    </tr>
  )
}

function Table({
  list,
  onOpenPatient,
  t,
}: {
  readonly list: WardListData
  readonly onOpenPatient: (patientId: string) => void
  readonly t: WardTranslate
}) {
  // One "today" for the whole render, so two rows cannot disagree about the date
  // if the table happens to render across midnight.
  const today = new Date()

  return (
    <table className="record-table ward-list">
      <caption className="visually-hidden">{t('ward:list.caption')}</caption>
      <thead>
        {/*
          Headings are abbreviated the way a Swedish ward list is: short enough that
          thirty rows fit on one screen, which is its own requirement (AGENTS.md §5 —
          the safety flags must be visible without scrolling). Expect a translation to
          be longer; §5 of I18N.md is why no column has a fixed width.
        */}
        <tr>
          <th scope="col">{t('ward:column.bed')}</th>
          <th scope="col">{t('ward:column.name')}</th>
          <th scope="col">{t('ward:column.identifier')}</th>
          <th scope="col">{t('ward:column.age')}</th>
          <th scope="col">{t('ward:column.admitted')}</th>
          <th scope="col">{t('ward:column.responsible')}</th>
          <th scope="col">{t('ward:column.safety')}</th>
        </tr>
      </thead>
      <tbody>
        {list.entries.map((entry) => (
          <Row
            key={entry.patientId}
            entry={entry}
            today={today}
            onOpenPatient={onOpenPatient}
            t={t}
          />
        ))}
      </tbody>
    </table>
  )
}

/**
 * The ward list for one ward, with the picker that chooses which.
 *
 * `ward` is required and has no default. It used to default to `PHASE_0_WARD`, which was a
 * second place that decided which ward phase 0 shows; now `App` owns the choice, because it
 * has to survive opening a patient and pressing Back.
 *
 * @param ward The ward code to show, or null before the directory has answered.
 * @param onOpenPatient Opens a patient's overview.
 * @param onWardChange Called with the code the clinician picked.
 */
export function WardListScreen({
  onOpenPatient,
  ward,
  onWardChange,
}: {
  readonly onOpenPatient: (patientId: string) => void
  readonly ward: string | null
  readonly onWardChange: (ward: string) => void
}) {
  // Both namespaces: the `common` values shared with every other patient screen, and
  // the ward list's own strings. **`common` first**, which is not cosmetic — it is
  // what makes this `t` assignable to `patient/patientDisplay.ts`'s narrower
  // `Translate`, so the same lookup renders a column heading here and a safety badge
  // there. Every key below names its namespace anyway.
  const { t } = useTranslation(['common', 'ward'])
  const [state, setState] = useState<State>({ kind: 'loading' })

  // Bumped by the retry button. The effect depends on it, which is the whole
  // mechanism — there is no imperative refetch to keep in step with the state.
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => {
    setState({ kind: 'loading' })
    setAttempt((value) => value + 1)
  }, [])

  useEffect(() => {
    // Nothing to fetch until the picker has a ward. The screen stays in `loading`, which is
    // what it is: the directory request is in flight one component down.
    if (ward === null) {
      return
    }

    const controller = new AbortController()

    fetchWardList(ward, controller.signal).then(
      (list) => {
        if (!controller.signal.aborted) {
          setState({ kind: 'loaded', list })
        }
      },
      (cause: unknown) => {
        if (controller.signal.aborted) {
          return
        }
        setState({
          kind: 'failed',
          failure: cause instanceof WardListError ? cause.failure : 'unavailable',
        })
      },
    )

    return () => controller.abort()
  }, [ward, attempt])

  return (
    <main className="ward">
      <header className="ward__header">
        <h1 className="ward__name">
          {/* The ward's name is record content and is shown as recorded. Until it
              arrives, the code is what we have — and it is what the clinician typed
              or read off the printed sheet, so it is the honest placeholder. Before
              the directory has answered there is no code either, and the heading is
              the generic word rather than an empty element. */}
          {state.kind === 'loaded' ? state.list.name : (ward ?? t('ward:list.heading'))}
        </h1>

        {/* Beside the heading, not in the app bar. Which ward is showing is a fact about
            this screen, and the app bar is where the banner and the signed-in clinician
            live — a ward selector up there would read as a global mode. */}
        <WardPicker selected={ward} onSelect={onWardChange} t={t} />
        {state.kind === 'loaded' && (
          <p className="ward__occupancy">
            {/*
              One ICU message, not a label concatenated with two numbers — I18N.md §8
              forbids assembling a sentence out of translated fragments, and "N of M
              beds occupied" is a sentence whose word order is a translator's
              decision. Two messages because `bedCount` is nullable: a ward whose bed
              count the record does not hold gets the count on its own, and the plural
              is ICU's rather than an `s` glued on.

              **Against a total, only patients with a bed occupy one.** Counting entries
              read "25 av 24 platser belagda" with one patient in a corridor (browser
              pass, 2026-10-07) — false whether or not 24 is a limit, because the
              25th has no bed. The rest are named as *utan plats*, inside the same
              message. Without a total the line counts patients admitted, and a
              patient with no bed is still admitted, so that message is unchanged.
            */}
            {state.list.bedCount === null
              ? t('ward:list.occupancyWithoutTotal', { occupied: state.list.entries.length })
              : t('ward:list.occupancy', {
                  occupied: state.list.entries.filter((entry) => entry.bed !== null).length,
                  total: state.list.bedCount,
                  withoutBed: state.list.entries.filter((entry) => entry.bed === null).length,
                })}
          </p>
        )}
      </header>

      {/* Announced, because the list replacing itself is the one thing on this
          screen that changes without the reader doing anything. */}
      <div aria-live="polite">
        {state.kind === 'loading' && <p className="ward__notice">{t('ward:list.loading')}</p>}

        {state.kind === 'failed' && (
          <div className="ward__notice ward__notice--error" role="alert">
            <p>
              {state.failure === 'not-found' ? t('ward:list.notFound') : t('ward:list.failed')}
            </p>

            {/* No retry for a ward that does not exist: the address is wrong, and a
                button that tries the same wrong address again teaches nothing. */}
            {state.failure === 'unavailable' && (
              <button type="button" onClick={retry}>
                {t('ward:list.retry')}
              </button>
            )}
          </div>
        )}

        {state.kind === 'loaded' &&
          (state.list.entries.length === 0 ? (
            <p className="ward__notice">{t('ward:list.empty')}</p>
          ) : (
            <Table list={state.list} onOpenPatient={onOpenPatient} t={t} />
          ))}
      </div>
    </main>
  )
}
